<template>
  <div class="wetheme-dropdown" ref="component" :class="['fit-' + this.fit, isOpen ? 'wetheme-dropdown--open' : 'wetheme-dropdown--closed', 'wetheme-dropdown--' + direction]">
    <label
      v-if="title"
      :for="id"
      v-text="title"
      @click="toggleDropdown"
      class="wetheme-dropdown__label"
    ></label>

    <div class="wetheme-dropdown__wrapper">
      <button
        class="alt-focus"
        ref="button"
        type="button"
        readonly="true"
        :id="id + '-button'"
        aria-haspopup="listbox"
        :aria-label="buttonLabel"
        @click="toggleDropdown"
        @keydown.enter.prevent="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
        @keydown.up.prevent="moveFocusedIndex(-1)"
        @keydown.down.prevent="moveFocusedIndex(1)"
        @keydown.esc.prevent="close"
      >
        <span v-text="hasStaticBtnText ? buttonLabel : selectedOption.label || '\xa0'"></span>
      </button>

      <transition name="wetheme-dropdown-fade">
        <ul
          :id="dropdownId"
          ref="ul"
          v-show="isOpen"
          role="listbox"
          :aria-activedescendant="id + '_' + focusedOption"
          tabindex="0"
          @keydown.up.prevent="moveFocusedIndex(-1)"
          @keydown.down.prevent="moveFocusedIndex(1)"
          @keydown.enter.prevent="applyFocused(false)"
          @keydown.space.prevent="applyFocused(false)"
          @blur="exitList"
          @keydown.esc.prevent="exitList"
        >
          <li
            role="option"
            v-for="option in options"
            :id="id + '_' + option.value"
            :key="option.value"
            :value="option.value"
            :class="{ selected: optionIsSelected(option) }"
            :aria-selected="optionIsSelected(option)"
            @click="toggleDropdown"
            @mousedown="optionClicked(option)"
            v-text="option.label"
          ></li>
        </ul>
      </transition>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

const getElementWidth = (el) => {
  var clone = el.cloneNode(true);
  clone.style.cssText = 'position: fixed; top: 0; left: 0; overflow: auto; visibility: hidden; pointer-events: none; height: unset; max-height: unset';
  document.body.appendChild(clone);
  var width = clone.getBoundingClientRect().width;
  document.body.removeChild(clone);
  return width;
}

let dropdown_last_id = 0;

export default Vue.extend({
  props: {
    fit: {
      type: String,
      default: 'parent',
      validator: function (value) {
        return ['parent', 'current', 'longest'].indexOf(value) !== -1
      },
    },
    direction: {
      type: String,
      default: 'down',
      validator: function (value) {
        return ['down', 'up'].indexOf(value) !== -1
      },
    },
  },
  data: function () {
    return {
      theme: null,
      id: '',
      title: '',
      isOpen: false,
      options: [],
      selectedOption: undefined,
      // focusedIndex is index of option that user navigated to using keyboard,
      // it's equal to index of selected option when keyboard was not used
      focusedIndex: 0,
      selectComponent: undefined,
    };
  },
  computed: {
    hasStaticBtnText: function() {
      if (this.id == 'sort-by') return true;
      return false;
    },
    buttonLabel: function() {
      if (this.hasStaticBtnText) return this.title;
      return `${this.title} ${this.selectedOption.label}`;
    },
    dropdownId: function () {
      return this.id + '-dropdown';
    },
    focusedOption: function () {
      return this.options[this.focusedIndex].value;
    }
  },
  beforeMount() {
    // Convert a <select> and <label> into options and other properties
    const select = this.$root.$el.querySelector('select');
    if (select.id) {
      this.id = select.id;
    } else {
      dropdown_last_id += 1;
      this.id = 'wetheme-dropdown-' + dropdown_last_id;
    }
    const labelEl = this.$root.$el.querySelector('label');
    this.title = labelEl ? labelEl.textContent : undefined;
    const options = [];
    Array.prototype.forEach.call(
      select.querySelectorAll('option'),
      function (option, index) {
        if (option.dataset.placeholder === undefined) {
          options.push({
            value: option.value,
            label: option.textContent,
          });
        }
        if (option.defaultSelected) {
          this.selectedOption = option;
          this.focusedIndex = index;

        }
      }.bind(this)
    );
    this.options = options;
    if (!this.selectedOption) {
      this.selectedOption = options[0];
      this.focusedIndex = 0;
    }
    this.selectWidth = getElementWidth(select);
    this.selectComponent = select;
    this.selectComponent.style.display = 'none';
    select.addEventListener('input', this.onSelectChange);
    select.addEventListener('change', this.onSelectChange);
  },
  mounted: function () {
    if (this.fit === 'longest') {
      const w = this.selectWidth + 20; // 20px for caret
      this.$el.style.width = w + 'px';
      this.$refs.ul.style.width = w + 'px';
    }
    this.$el.appendChild(this.selectComponent);
  },
  methods: {
    findOptionIndex: function (option) {
      let index;
      this.options.forEach(function (o, i) {
        if (o.value === option.value) {
          index = i;
        }
      });
      return index;
    },
    optionClicked: function (option) {
      this.selectedOption = option;
      this.focusedIndex = this.findOptionIndex(option);
      this.selectComponent.value = option.value;
      this.$nextTick(function () {
        // Emit the event when input value is already changed
        const e = new CustomEvent('change');
        this.selectComponent.dispatchEvent(e);
      });
    },
    moveFocusedIndex: function (offset) {
      const focusedIndex = this.focusedIndex + offset;

      if (focusedIndex >= 0 && focusedIndex < this.options.length) {
        this.focusedIndex = focusedIndex;
      }

      // document.getElementById( this.options[this.focusedIndex].value ).focus();
      if (!this.isOpen) {
        this.applyFocused(true);
      }
    },
    applyFocused: function (force) {
      if (!this.isOpen && !force) {
        this.isOpen = true;
        return;
      }
      this.optionClicked(this.options[this.focusedIndex]);
      this.isOpen = false;
    },
    close: function () {
      this.isOpen = false;
    },
    focusList: function() {
      this.$nextTick(function () {
        this.$refs.ul.focus();
      })
    },
    exitList: function() {
      this.isOpen = false;
      this.$refs.button.focus();
    },
    toggleDropdown: function () {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.focusList();
      } else {
        this.$refs.button.focus();
      }
    },
    onSelectChange: function (e) {
      const val = this.selectComponent.value;
      if (val !== this.selectedOption.value) {
        let index;
        let option;
        this.options.forEach((o, i) => {
          if (val === o.value) {
            index = i;
            option = o;
          }
        });
        this.selectedOption = option;
        this.focusedIndex = index;
      }
    },
    optionIsSelected: function (option) {
      return option.value === this.options[this.focusedIndex].value
    }
  },
});

</script>
